import { Slider } from '@mui/material';
import { ArrowUUpLeft, Check } from '@phosphor-icons/react';
import { Button, DatePicker, Select, Tag, VText, cx } from '@vake/ui-kit';
import { FC, SyntheticEvent } from 'react';

type ResetProps = {
    description: string;
    resetFunction: () => void;
};
const ResetWrapper: FC<ResetProps> = ({ description, resetFunction }) => {
    return (
        <div className="flex justify-between">
            <VText>{description}</VText>
            <Button text="Reset" iconAfter={<ArrowUUpLeft />} variant="subtle" size="xsmall" onClick={resetFunction} />
        </div>
    );
};

type CalendarProps = {
    subscriptionStart: Date;
    startDate: Date | null;
    endDate: Date | null;
    setStartDate: (date: Date | null) => void;
    setEndDate: (date: Date | null) => void;
};
export type Props = {
    darkDetectionConfidence: number;
    setDarkDetectionConfidence: (value: number) => void;
    defaultConfidence: number;
    verifiedActive: boolean;
    darkActive: boolean;
    unCorrelatedActive: boolean;
    setVerifiedActive: (value: boolean) => void;
    setDarkActive: (value: boolean) => void;
    setUnCorrelatedActive: (value: boolean) => void;
    numVerified: number;
    numDark: number;
    numUncorrelated: number;
    tags: Tag[];
    setTags: (value: Tag[]) => void;
    calendarProps: CalendarProps;
    tagFilterStrategy: string;
    setTagFilterStrategy: (value: string) => void;
};
export const Filters: FC<Props> = ({
    darkDetectionConfidence,
    setDarkDetectionConfidence,
    defaultConfidence,
    verifiedActive,
    darkActive,
    unCorrelatedActive,
    setVerifiedActive,
    setDarkActive,
    setUnCorrelatedActive,
    numVerified,
    numDark,
    numUncorrelated,
    tags,
    setTags,
    calendarProps,
    tagFilterStrategy,
    setTagFilterStrategy,
}) => {
    const tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 1);
    const updateTag = (index: number) => {
        const newTags = [...tags];
        newTags[index].subtle = !tags[index].subtle;
        setTags(newTags);
    };
    const resetTags = () => {
        setTags(
            tags.map((tag) => ({
                ...tag,
                subtle: true,
            }))
        );
    };
    const handleSliderChange = (e: Event | SyntheticEvent, newValue: number | number[]) => {
        if (!Array.isArray(newValue)) {
            setDarkDetectionConfidence(newValue);
        }
    };
    const resetConfidence = () => {
        setDarkDetectionConfidence(defaultConfidence);
    };
    const resetDetectionType = () => {
        setVerifiedActive(false);
        setDarkActive(false);
        setUnCorrelatedActive(false);
    };
    const resetCalendar = () => {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0);
        calendarProps.setStartDate(startOfToday);
        calendarProps.setEndDate(tomorrow);
    };
    const resetAllFilters = () => {
        resetConfidence();
        resetDetectionType();
        resetTags();
        resetCalendar();
    };
    return (
        <div className="p-4">
            <div className="py-2">
                <ResetWrapper description="Date Interval" resetFunction={resetCalendar} />
                <div className="flex gap-2">
                    <DatePicker
                        selectedDate={calendarProps.startDate}
                        setSelectedDate={calendarProps.setStartDate}
                        minDate={calendarProps.subscriptionStart}
                        maxDate={tomorrow}
                        popperPlacement="bottom-start"
                    />
                    <DatePicker
                        selectedDate={calendarProps.endDate}
                        setSelectedDate={(date) => {
                            date?.setHours(23, 59);
                            calendarProps.setEndDate(date);
                        }}
                        minDate={calendarProps.subscriptionStart}
                        maxDate={tomorrow}
                    />
                </div>
            </div>
            <div className="py-2">
                <ResetWrapper description="Dark Detection Confidence" resetFunction={resetConfidence} />
                <Slider
                    value={darkDetectionConfidence}
                    min={0}
                    max={100}
                    step={1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => <div>{`${value}%`}</div>}
                    onChange={handleSliderChange}
                />
            </div>
            <div className="py-2">
                <ResetWrapper description="Detection Type" resetFunction={resetDetectionType} />
                <button
                    className={cx('border rounded p-1 border-green-400 m-1', verifiedActive ? 'bg-green-400' : '')}
                    onClick={() => setVerifiedActive(!verifiedActive)}
                >
                    <VText>Verified ({numVerified})</VText>
                </button>
                <button
                    className={cx('border rounded p-1 border-red-400 m-1', darkActive ? 'bg-red-400' : '')}
                    onClick={() => setDarkActive(!darkActive)}
                >
                    <VText>Dark Vessel ({numDark})</VText>
                </button>
                <button
                    className={cx('border rounded p-1 border-blue-400 m-1', unCorrelatedActive ? 'bg-blue-400' : '')}
                    onClick={() => setUnCorrelatedActive(!unCorrelatedActive)}
                >
                    <VText>Uncorrelated AIS ({numUncorrelated})</VText>
                </button>
            </div>
            <div className="py-2">
                <ResetWrapper description="Tags" resetFunction={resetTags} />
                <div className="flex flex-wrap px-3 py-2 gap-2 mb-2">
                    {tags &&
                        tags.map((tag, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    updateTag(index);
                                }}
                            >
                                <Tag {...tag} />
                            </button>
                        ))}
                </div>
                <div className="flex items-center gap-2">
                    <VText variant="B2">Require</VText>
                    <Select
                        options={[
                            {
                                icon: <VText variant="B2">ANY</VText>,
                                value: 'or',
                            },
                            {
                                icon: <VText variant="B2">ALL</VText>,
                                value: 'and',
                            },
                        ]}
                        onChange={setTagFilterStrategy}
                        selectedValue={tagFilterStrategy}
                    />
                    <VText variant="B2">tags to be present</VText>
                </div>
            </div>
            <div className="py-2">
                <div className="">
                    <Button
                        size="small"
                        variant="neutral"
                        text="Remove all filters"
                        iconBefore={<Check size={16} />}
                        onClick={resetAllFilters}
                    />
                </div>
            </div>
        </div>
    );
};
